/**
 * Waterfall timeline diagram for a single trace.
 *
 * Replaces the earlier react-flow horizontal flowchart. A trace is a sequence
 * of steps with timing, so a Gantt/waterfall view (X = time, Y = sequence)
 * conveys order and duration at a glance — the patterns engineers already
 * know from X-Ray, Honeycomb, Datadog APM, Sentry.
 *
 * Each row:
 *   [depth indent] [status dot] [stepId · kind chip · fn (lambda)]   [bar offset/width by timing]
 *
 * Click a row → onStepClick(step) opens the detail drawer (existing).
 */
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material"
import { FC, useMemo } from "react"

import { fmtDuration } from "./formatters"
import { getKindStyle } from "./traceColors"
import type { TraceStepItem } from "./types"

// ---------------------------------------------------------------------------
// Source breadcrumb formatter
// ---------------------------------------------------------------------------

/**
 * Render a step's source as `<basename(file)>/<fn>[/<qualifier>]`. Drops any
 * field that's missing so a partial source still produces something readable.
 */
const formatSourceBreadcrumb = (
  source: TraceStepItem["source"],
): string | null => {
  if (!source) return null
  const fileBasename = source.file ? source.file.split("/").pop() : undefined
  const parts = [fileBasename, source.fn, source.qualifier].filter(
    (p): p is string => Boolean(p),
  )
  return parts.length > 0 ? parts.join("/") : null
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

type LaidOutStep = {
  step: TraceStepItem
  depth: number
  startMs: number // offset from trace start
  durationMs: number // duration of this step (clamped >=1ms for visibility)
}

const computeLayout = (steps: TraceStepItem[]): {
  rows: LaidOutStep[]
  totalMs: number
  startEpoch: number
} => {
  if (steps.length === 0) {
    return { rows: [], totalMs: 0, startEpoch: 0 }
  }

  // Sort by stepSequence so the row order matches execution order.
  const sorted = [...steps].sort((a, b) => a.stepSequence - b.stepSequence)

  // Build a depth lookup via parentStepId chains.
  const stepById = new Map<string, TraceStepItem>()
  sorted.forEach((s) => stepById.set(s.stepId, s))

  const depthCache = new Map<string, number>()
  const computeDepth = (s: TraceStepItem): number => {
    if (depthCache.has(s.stepId)) return depthCache.get(s.stepId)!
    if (!s.parentStepId) {
      depthCache.set(s.stepId, 0)
      return 0
    }
    const parent = stepById.get(s.parentStepId)
    if (!parent) {
      depthCache.set(s.stepId, 0)
      return 0
    }
    const d = computeDepth(parent) + 1
    depthCache.set(s.stepId, d)
    return d
  }

  const startEpoch = new Date(sorted[0].startedAt).getTime()
  let endEpoch = startEpoch
  sorted.forEach((s) => {
    const start = new Date(s.startedAt).getTime()
    const end = s.completedAt
      ? new Date(s.completedAt).getTime()
      : start + (s.durationMs ?? 0)
    if (end > endEpoch) endEpoch = end
  })
  const totalMs = Math.max(1, endEpoch - startEpoch)

  const rows: LaidOutStep[] = sorted.map((s) => ({
    step: s,
    depth: computeDepth(s),
    startMs: new Date(s.startedAt).getTime() - startEpoch,
    // Clamp very-short steps to 1ms so the bar is visible.
    durationMs: Math.max(1, s.durationMs ?? 0),
  }))

  return { rows, totalMs, startEpoch }
}

// Pick a sensible tick spacing for the time axis.
const computeTicks = (totalMs: number): number[] => {
  // Aim for ~5 ticks across the axis.
  const target = totalMs / 5
  // Round to a "nice" number: 1, 2, 5, 10, 20, 50, 100, ...
  const pow = Math.pow(10, Math.floor(Math.log10(Math.max(1, target))))
  const candidates = [1, 2, 5, 10].map((m) => m * pow)
  const step = candidates.find((c) => c >= target) ?? candidates[candidates.length - 1]
  const ticks: number[] = []
  for (let t = 0; t <= totalMs + 1; t += step) ticks.push(t)
  return ticks
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

const STATUS_DOT_COLOR: Record<TraceStepItem["status"], string> = {
  succeeded: "#22c55e",
  failed: "#ef4444",
  skipped: "#d1d5db",
  started: "#fbbf24",
}

const StatusDot: FC<{ status: TraceStepItem["status"] }> = ({ status }) => (
  <Box
    sx={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
      bgcolor: STATUS_DOT_COLOR[status],
      ...(status === "started"
        ? {
            animation: "sstv2TraceDotPulse 1.2s ease-in-out infinite",
            "@keyframes sstv2TraceDotPulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.4 },
            },
          }
        : {}),
    }}
  />
)

const TimeAxis: FC<{ totalMs: number; leftWidthPct: number }> = ({
  totalMs,
  leftWidthPct,
}) => {
  const ticks = computeTicks(totalMs)
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: 24,
        position: "sticky",
        top: 0,
        bgcolor: "white",
        zIndex: 1,
        borderBottom: "1px solid",
        borderColor: "rgba(0, 0, 0, 0.08)",
      }}
    >
      <Box sx={{ width: `${leftWidthPct}%`, pr: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
          step
        </Typography>
      </Box>
      <Box sx={{ flex: 1, position: "relative", height: "100%" }}>
        {ticks.map((t) => {
          const leftPct = (t / totalMs) * 100
          return (
            <Box
              key={t}
              sx={{
                position: "absolute",
                left: `${leftPct}%`,
                top: 0,
                bottom: 0,
                borderLeft: "1px dashed",
                borderColor: "rgba(0, 0, 0, 0.08)",
                pl: 0.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace", fontSize: 10 }}
              >
                {fmtDuration(t)}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

const StepRow: FC<{
  laid: LaidOutStep
  totalMs: number
  selected: boolean
  onClick: (step: TraceStepItem) => void
  leftWidthPct: number
}> = ({ laid, totalMs, selected, onClick, leftWidthPct }) => {
  const { step, depth, startMs, durationMs } = laid
  const kindStyle = getKindStyle(step.kind)
  const leftPct = (startMs / totalMs) * 100
  const widthPct = Math.max(0.4, (durationMs / totalMs) * 100)
  const isFailed = step.status === "failed"

  const breadcrumb = formatSourceBreadcrumb(step.source)
  const lambdaLabel = step.source?.lambda
  const fileLabel = step.source?.file
  const fnLabel = step.source?.fn

  // Decide where the bar's duration label lives:
  //   - `inside`: bar is wide enough that "523 ms" / "1.62 s" fits.
  //   - `outsideRight`: narrow bar with room to its right.
  //   - `outsideLeft`: narrow bar near the right edge — push label left.
  // Threshold is approximate (no DOM measurement); chosen so any label up to
  // ~6 chars (`523 ms`, `1.62 s`) reliably fits on a typical ~700 px bar area.
  // Tiny bars (1–14 ms steps) consistently overflow.
  const durationLabel = fmtDuration(durationMs)
  const labelInside = widthPct >= 8
  const labelPlacement: "inside" | "outsideRight" | "outsideLeft" = labelInside
    ? "inside"
    : leftPct + widthPct > 90
      ? "outsideLeft"
      : "outsideRight"

  return (
    <Box
      onClick={() => onClick(step)}
      sx={{
        display: "flex",
        alignItems: "stretch",
        height: 52,
        cursor: "pointer",
        borderRadius: 0.75,
        borderBottom: "1px solid",
        borderColor: "rgba(0, 0, 0, 0.05)",
        ...(selected
          ? { bgcolor: "rgba(99, 102, 241, 0.10)" }
          : { "&:hover": { bgcolor: "rgba(0, 0, 0, 0.03)" } }),
      }}
    >
      {/* Left: step label area, two lines (stepId+chip on top, breadcrumb below) */}
      <Box
        sx={{
          width: `${leftWidthPct}%`,
          pr: 1,
          pl: 0.5 + depth * 1.5,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 0.25,
          minWidth: 0,
        }}
      >
        {/* Line 1: dot + stepId + kind chip */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <StatusDot status={step.status} />
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              fontSize: 12,
              fontWeight: isFailed ? 600 : 500,
              color: isFailed ? "#7f1d1d" : "inherit",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flexShrink: 1,
              minWidth: 0,
            }}
            title={step.stepId}
          >
            {step.stepId}
          </Typography>
          <Chip
            size="small"
            label={kindStyle.label}
            sx={{
              height: 18,
              fontSize: 10,
              bgcolor: kindStyle.background,
              color: kindStyle.color,
              fontWeight: 600,
              flexShrink: 0,
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        </Box>

        {/* Line 2: breadcrumb (file/fn/qualifier) — indented to align under stepId */}
        {breadcrumb && (
          <Tooltip
            title={
              <Stack spacing={0.25}>
                {fileLabel && (
                  <span style={{ fontFamily: "monospace" }}>{fileLabel}</span>
                )}
                {fnLabel && (
                  <span style={{ fontFamily: "monospace", opacity: 0.85 }}>
                    {fnLabel}()
                  </span>
                )}
                {lambdaLabel && (
                  <span style={{ fontFamily: "monospace", opacity: 0.7 }}>
                    Lambda: {lambdaLabel}
                  </span>
                )}
              </Stack>
            }
            arrow
            placement="bottom-start"
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: "monospace",
                fontSize: 11,
                color: "text.secondary",
                fontStyle: "italic",
                pl: 1.75,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: 1.2,
              }}
            >
              {breadcrumb}
            </Typography>
          </Tooltip>
        )}
        {!breadcrumb && lambdaLabel && (
          <Typography
            variant="caption"
            sx={{
              fontFamily: "monospace",
              fontSize: 11,
              color: "text.secondary",
              fontStyle: "italic",
              pl: 1.75,
              lineHeight: 1.2,
            }}
          >
            {lambdaLabel}
          </Typography>
        )}
      </Box>

      {/* Right: timing bar */}
      <Box sx={{ flex: 1, position: "relative", height: "100%" }}>
        {/* Faint baseline */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "50%",
            height: 1,
            bgcolor: "rgba(0, 0, 0, 0.03)",
          }}
        />
        {/* The bar — explicit height (rather than top/bottom %) prevents the
            label's line-height from forcing the bar taller than the row,
            which produced visual overlap with adjacent rows. */}
        <Box
          sx={{
            position: "absolute",
            left: `${leftPct}%`,
            width: `${widthPct}%`,
            top: "50%",
            transform: "translateY(-50%)",
            height: 22,
            bgcolor: kindStyle.background,
            border: `1px solid ${isFailed ? "#ef4444" : "rgba(0, 0, 0, 0.15)"}`,
            borderRadius: 0.5,
            display: "flex",
            alignItems: "center",
            px: 0.5,
            minWidth: 4,
            overflow: "hidden",
          }}
        >
          {labelPlacement === "inside" && (
            <Typography
              variant="caption"
              sx={{
                color: kindStyle.color,
                fontFamily: "monospace",
                fontSize: 10,
                fontWeight: 600,
                whiteSpace: "nowrap",
                lineHeight: 1.1,
              }}
            >
              {durationLabel}
            </Typography>
          )}
        </Box>
        {labelPlacement === "outsideRight" && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              left: `${leftPct + widthPct}%`,
              top: "50%",
              transform: "translateY(-50%)",
              ml: 0.75,
              color: "text.secondary",
              fontFamily: "monospace",
              fontSize: 10,
              fontWeight: 600,
              whiteSpace: "nowrap",
              lineHeight: 1.1,
              pointerEvents: "none",
            }}
          >
            {durationLabel}
          </Typography>
        )}
        {labelPlacement === "outsideLeft" && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              right: `${100 - leftPct}%`,
              top: "50%",
              transform: "translateY(-50%)",
              mr: 0.75,
              color: "text.secondary",
              fontFamily: "monospace",
              fontSize: 10,
              fontWeight: 600,
              whiteSpace: "nowrap",
              lineHeight: 1.1,
              pointerEvents: "none",
            }}
          >
            {durationLabel}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export type Sstv2TraceTimelineProps = {
  steps: TraceStepItem[]
  onStepClick: (step: TraceStepItem) => void
  selectedStepSortKey?: string
  /** Width % of the row dedicated to the left label area. Default 40. */
  leftWidthPct?: number
}

const Sstv2TraceTimeline: FC<Sstv2TraceTimelineProps> = ({
  steps,
  onStepClick,
  selectedStepSortKey,
  leftWidthPct = 40,
}) => {
  const { rows, totalMs } = useMemo(() => computeLayout(steps), [steps])

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          color: "text.secondary",
          border: "1px dashed",
          borderColor: "rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
        }}
      >
        <Typography variant="body2">No steps recorded yet.</Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        bgcolor: "white",
        border: "1px solid",
        borderColor: "rgba(0, 0, 0, 0.08)",
        borderRadius: 1,
        overflow: "auto",
        maxHeight: 720,
      }}
    >
      <TimeAxis totalMs={totalMs} leftWidthPct={leftWidthPct} />
      <Box sx={{ py: 0.5 }}>
        {rows.map((laid) => (
          <StepRow
            key={laid.step.sk}
            laid={laid}
            totalMs={totalMs}
            selected={selectedStepSortKey === laid.step.sk}
            onClick={onStepClick}
            leftWidthPct={leftWidthPct}
          />
        ))}
      </Box>
    </Box>
  )
}

export default Sstv2TraceTimeline
